import { modelOptions, PropType, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { SensorType } from "../../shared.js";
import { milliseconds } from "../../timeConstants.js";
import { getZodSchemaFromClass, SensorTypeString, validate } from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import {
    SensorReadingConfigurationFieldsWithDefaults,
    SensorReadingConfigurationSchemaTypes,
} from "../types/sensorReadingConfiguration.js";
import { DtoValidatorTypes } from "../validatableClass.js";
import { CustomSensorTimingRule } from "./subSchemas/customSensorTimingRule.js";
import { SensorTypeReadingConfig } from "./subSchemas/sensorTypeReadingConfig.js";
import * as utilities from "./utilities.js";

const SCHEMA_KEY = "SensorReadingConfiguration" as const;

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<SensorReadingConfiguration>>(...addSchemaKeyHook(SCHEMA_KEY))
export class SensorReadingConfiguration extends SchemaBase<
    SensorReadingConfiguration,
    SensorReadingConfigurationFieldsWithDefaults
> {
    static schemaKey = SCHEMA_KEY;
    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({
        childClass: SensorTypeReadingConfig,
        childClassTransform: (object) => z.partialRecord(SensorTypeString, object).optional(),
    })
    @prop({ type: () => SensorTypeReadingConfig, required: true, default: {} }, PropType.MAP)
    public configurationBySensorType: Partial<Record<SensorType, SensorTypeReadingConfig>> = {};

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public readingPeriod?: milliseconds;

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public uplinkPeriod?: milliseconds;

    @validate({
        childClass: CustomSensorTimingRule,
        childClassTransform: (object) => z.array(object).optional(),
    })
    @prop({ type: () => CustomSensorTimingRule, required: true, default: [] }, PropType.ARRAY)
    public customRules: CustomSensorTimingRule[] = [];

    static getters = {} as const satisfies Record<string, (self: SensorReadingConfiguration) => any>;

    static utilities = {
        ...utilities,
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SensorReadingConfigurationSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<SensorReadingConfiguration, SensorReadingConfigurationFieldsWithDefaults>,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

const SensorReadingConfigurationSchema = getZodSchemaFromClass(SensorReadingConfiguration);

SensorReadingConfiguration.dtoValidators =
    SensorReadingConfigurationSchema as unknown as DtoValidatorTypes<SensorReadingConfigurationSchemaTypes>;
